import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Typography,
  Upload,
  message,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { compareReconFile } from '../services/api';

const { Title, Text } = Typography;

const ReconPage = () => {
  const [fileList, setFileList] = useState([]);
  const [reconDate, setReconDate] = useState(dayjs());
  const [custodian, setCustodian] = useState('mcb');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const selectedFile = fileList[0]?.originFileObj || null;

  const mismatchColumns = useMemo(
    () => [
      {
        title: 'Custodian Portfolio Code',
        dataIndex: 'custodian_account_code',
        key: 'custodian_account_code',
        width: 220,
      },
      {
        title: 'Portfolio',
        key: 'portfolio_name',
        render: (_, row) => row.portfolio_name || 'Not mapped',
      },
      {
        title: 'ISIN',
        dataIndex: 'isin',
        key: 'isin',
        width: 150,
      },
      {
        title: 'Recon Qty',
        dataIndex: 'recon_quantity',
        key: 'recon_quantity',
        align: 'right',
      },
      {
        title: 'System Qty',
        dataIndex: 'system_quantity',
        key: 'system_quantity',
        align: 'right',
      },
      {
        title: 'Diff (Recon - System)',
        dataIndex: 'difference',
        key: 'difference',
        align: 'right',
      },
    ],
    []
  );

  const handleCompare = async () => {
    if (!selectedFile) {
      message.warning('Select a recon file first');
      return;
    }
    if (!reconDate) {
      message.warning('Select a recon date');
      return;
    }

    try {
      setLoading(true);
      const data = await compareReconFile({
        file: selectedFile,
        reconDate: reconDate.format('YYYY-MM-DD'),
        custodian,
      });
      setResult(data);
      message.success('Recon comparison completed');
    } catch (error) {
      const apiError = error?.response?.data;
      if (apiError?.error) {
        message.error(apiError.error);
      } else {
        message.error('Failed to compare recon file');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div>
          <Title level={4} style={{ marginBottom: 4 }}>
            Custodian Recon
          </Title>
          <Text type="secondary">
            Upload a custodian recon file, select the recon date, and compare positions to system holdings.
          </Text>
        </div>

        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={8}>
            <Select
              value={custodian}
              onChange={setCustodian}
              options={[
                { value: 'mcb', label: 'MCB' },
                { value: 'other', label: 'Other' },
              ]}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} md={8}>
            <DatePicker
              value={reconDate}
              onChange={setReconDate}
              style={{ width: '100%' }}
              format="YYYY-MM-DD"
            />
          </Col>
          <Col xs={24} md={8}>
            <Upload
              accept=".csv,.txt"
              maxCount={1}
              fileList={fileList}
              beforeUpload={() => false}
              onChange={({ fileList: next }) => setFileList(next)}
            >
              <Button icon={<UploadOutlined />} block>
                Select Recon File
              </Button>
            </Upload>
          </Col>
        </Row>

        <Row>
          <Col>
            <Button type="primary" loading={loading} onClick={handleCompare}>
              Compare to System Holdings
            </Button>
          </Col>
        </Row>

        {result?.unmapped_recon_portfolios?.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message="Unmapped recon portfolios"
            description={result.unmapped_recon_portfolios.join(', ')}
          />
        )}

        {result && (
          <>
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={12} md={8} lg={4}>
                <Card size="small">
                  <Statistic title="File Rows" value={result.summary.rows_in_file} />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8} lg={4}>
                <Card size="small">
                  <Statistic title="Recon Keys" value={result.summary.recon_position_keys} />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8} lg={4}>
                <Card size="small">
                  <Statistic title="System Keys" value={result.summary.system_position_keys} />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8} lg={4}>
                <Card size="small">
                  <Statistic title="Matched" value={result.summary.matched_keys} />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8} lg={4}>
                <Card size="small">
                  <Statistic title="Mismatches" value={result.summary.mismatch_keys} />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8} lg={4}>
                <Card size="small">
                  <Statistic title="Mapped Portfolios" value={result.summary.mapped_portfolios} />
                </Card>
              </Col>
            </Row>

            <Table
              rowKey={(row) => `${row.custodian_account_code}-${row.isin}`}
              columns={mismatchColumns}
              dataSource={result.mismatches || []}
              pagination={{ pageSize: 25 }}
              locale={{ emptyText: 'No quantity mismatches found for this upload/date.' }}
            />
          </>
        )}
      </Space>
    </Card>
  );
};

export default ReconPage;
